import {TaskDurum} from "@/plugins/uyap-plugin/enum/TaskDurum";
import {TaskTuru} from "@/plugins/uyap-plugin/enum/TaskTuru";
import {
    ACILMIS_TAKIP_STATUS,
    isAcilmisTakipHatasi,
    isOdemeDeneniyorHatasi,
    ODEME_DENENIYOR_STATUS,
    TEVZI_STATUS
} from "@/plugins/uyap-plugin/takip_ac/AcilmisTakipHatasi";
import {ACILMIS_TAKIP_SILME_KISITI_AKTIF, ACILMIS_TAKIP_SILME_UYARISI} from "@/helpers/HashSilmeKisitiHelper";

/**
 * DEV-3302 — "Hash Listesinden Sil" çöp kutusunun ortak kuralları.
 *
 * NEDEN: ikon görevin hiç değişmeyen verisine (data.parsedFile.hash) bakıyordu; hash silinse de
 * F5 / yeniden giriş / başka kullanıcıda geri geliyordu (TDT-2505, TDT-2640, TDT-3481).
 *   - child-tasks?hash_kaydi=1 her satıra hash_kaydi = {hash, aktif, status} | null koyar
 *     (backend HashKaydiDurumuService). İkon yalnız BITTI_HATALI + aktif kayıtta çıkar.
 *   - Ödeme görevlerinde (takip_ac, mts_takip_ac) ikon yok: tevzi almazlar, hash kontrolü yapmazlar.
 *   - Silme başarılı olunca aynı hash'li BITTI_HATALI satırlar GÖREV ID'siyle işaretlenir;
 *     işaret, silmeden SONRA başlamış bir child-tasks yanıtı gelene kadar kalır.
 *   - Anahtar hiç yoksa (eski backend / güncellenmemiş on-prem) bugünkü kurala düşülür ve
 *     işaretler sayfadan çıkana kadar kalır.
 */

export const CHILD_TASKS_HASH_PARAMETRESI = "hash_kaydi=1";

/** Silme onayı gövdesi (başlık DeleteDialog'da "Emin misiniz?" kalır). Kayıt durumu bilinmiyorsa. */
export const HASH_SILME_UYARISI_GENEL =
    "Bu dosyanın hash kaydı silinecek. Görev ve UYAP'taki dosya silinmez; yalnızca bu dosya için " +
    "mükerrer takip kontrolü kalkar ve dosya yeniden gönderilebilir.";

/** Kayıt tevzi durumunda: silmek Ödeme Bekleyenler / MTS Takip Aç eşleşmesini koparır. */
export const HASH_SILME_UYARISI_TEVZI =
    "Bu dosya için UYAP'tan daha önce tevzi alınmış. Hash kaydını silerseniz mükerrer takip kontrolü " +
    "kalkar ve yeniden denemede yeni bir tevzi alınır. Eski tevzi UYAP'ta tamamlanmayan dosya olarak " +
    "kalır ve Ödeme Bekleyenler / MTS Takip Aç ekranlarında eşleşmez.";

/** Kayıt odeme_deneniyor durumunda: takip açılmış olabilir. */
export const HASH_SILME_UYARISI_ODEME_DENENIYOR =
    "Bu takip için ödeme denenmiş, sonucu alınamamış; takip UYAP'ta açılmış olabilir. Silmeden önce " +
    "UYAP'tan kontrol ediniz. Hash kaydı silinirse aynı dosya yeniden gönderildiğinde mükerrer kontrolü " +
    "çalışmaz; aynı borç için ikinci bir takip açılabilir ve harç iki kez ödenebilir.";

export const HASH_SILINDI_MESAJI = "Hash kaydı silindi.";
export const HASH_KAYDI_BULUNAMADI_MESAJI = "Hash kaydı bulunamadı; daha önce silinmiş olabilir.";
export const HASH_SILINEMEDI_MESAJI = "Hash kaydı silinemedi.";
export const SUNUCUYA_ULASILAMADI_MESAJI = "Sunucuya ulaşılamadı.";

/**
 * Mükerrer (tevzi alınmış) hata mesajının ortak parçası. Eski ("Seçili takip daha önce tevzi
 * almıştır.") ve yeni ("Bu dosya için daha önce tevzi alınmış (mükerrer kayıt).") metinde de var.
 */
export const MUKERRER_TEVZI_HATA_ISARETI = "daha önce tevzi al";

/** Ödeme görevleri: tevzi almaz, hash kontrolü yapmaz; hatalı satırda çöp kutusu yok (DEV-3302 karar 9). */
const ODEME_GOREV_TURLERI: number[] = [TaskTuru.takip_ac, TaskTuru.mts_takip_ac];

/** Onay uyarısının önceliği: yalnız yukarı çıkılabilir (genel < tevzi < odeme_deneniyor < acildi). */
const KAYIT_DURUMU_DERECESI: Record<string, number> = {
    [TEVZI_STATUS]: 1,
    [ODEME_DENENIYOR_STATUS]: 2,
    [ACILMIS_TAKIP_STATUS]: 3
};

export interface HashKaydi {
    hash: string;
    aktif: boolean;
    status: string | null;
}

/** DELETE /api/v1/uyap-takip-hash yanıtının data kısmı. silindi yalnız yeni backend'de var. */
export interface HashSilmeYaniti {
    success: boolean;
    silindi?: boolean;
    message?: string;
}

export interface HashSilmeSonucu {
    tamam: boolean;
    toast: "success" | "info" | "warning";
    mesaj: string;
}

/** Görev id -> işaretlendiği anda verilmiş son yükleme numarası. */
export type HashSilmeIsaretleri = Record<string, number>;

export function childTasksUrl(parentId: string | number): string {
    return "/api/v1/task/" + parentId + "/child-tasks?" + CHILD_TASKS_HASH_PARAMETRESI;
}

/** Yeni backend parametre gelince anahtarı HER satıra koyar (değer null olabilir). */
export function hashKaydiGeldi(item: any): boolean {
    return item != null && Object.prototype.hasOwnProperty.call(item, "hash_kaydi");
}

function doluMetin(deger: any): deger is string {
    return typeof deger === "string" && deger.trim() !== "";
}

/**
 * Silinecek hash. Sunucu çözdüyse onun değeri (kontrol edilen = silinen); yoksa backend
 * hashCoz ile AYNI sıra: önce data.hash (eski MTS'te data doğrudan parsedFile), sonra
 * data.parsedFile.hash.
 */
export function gorevHash(item: any): string | null {
    const kayit: HashKaydi | null = hashKaydiGeldi(item) ? item.hash_kaydi : null;
    if (kayit && doluMetin(kayit.hash))
        return kayit.hash;
    const data = item ? item.data : null;
    if (!data || typeof data !== "object")
        return null;
    if (doluMetin(data.hash))
        return data.hash;
    return data.parsedFile && doluMetin(data.parsedFile.hash) ? data.parsedFile.hash : null;
}

/**
 * Üst görevin türü (satırda task_type_id yoksa ödeme görevi kontrolünün yedeği). Store'daki
 * getGorevler listesinden id ile bulunur. Kimlikler Mongo ObjectId metni olduğu için metin olarak
 * karşılaştırılır (dört detay view'ındaki paternTask `Number(id)` ile aradığı için bulamıyor). Yoksa null.
 */
export function ebeveynGorevTuru(gorevler: any, parentId: string | number | null | undefined): number | null {
    if (!gorevler || parentId == null || parentId === "")
        return null;
    const aranan = String(parentId);
    const liste: any[] = [...(gorevler.incomplete_tasks || []), ...(gorevler.completed_tasks || [])];
    const ebeveyn = liste.find(gorev => gorev && gorev.id != null && String(gorev.id) === aranan);
    return ebeveyn && ebeveyn.task_type_id != null ? Number(ebeveyn.task_type_id) : null;
}

/** Backend is_numeric ile aynı yorum: sayı ya da sayı içeren (boş olmayan) metin. */
function sayisalMi(deger: any): boolean {
    if (typeof deger === "number")
        return isFinite(deger);
    return typeof deger === "string" && deger.trim() !== "" && isFinite(Number(deger));
}

/**
 * Durum metin olarak da gelebilir ("4"); backend adayMi gibi sayıya çevrilerek karşılaştırılır
 * (DEV-3302: iki taraf aynı satır için farklı karar vermesin).
 */
function bittiHataliMi(item: any): boolean {
    return sayisalMi(item.status) && Number(item.status) === TaskDurum.BITTI_HATALI;
}

/** Satırın türü yoksa ya da sayı değilse üst görevin türüne bakılır (backend adayMi ile aynı). */
export function odemeGoreviMi(item: any, ebeveynTuru?: number | null): boolean {
    const tur = item && sayisalMi(item.task_type_id) ? item.task_type_id : ebeveynTuru;
    return tur != null && ODEME_GOREV_TURLERI.indexOf(Number(tur)) !== -1;
}

function isaretli(item: any, isaretler: HashSilmeIsaretleri): boolean {
    return item.id != null && isaretler[String(item.id)] !== undefined;
}

/**
 * Çöp kutusu: BITTI_HATALI + ödeme görevi değil + bu ekranda silinmemiş +
 * (yeni backend: aktif kayıt | eski backend: hash dolu). YENIDEN_BASLATILDI satırlarında yok.
 */
export function hashSilmeIkonuGorunur(item: any, isaretler: HashSilmeIsaretleri, ebeveynTuru?: number | null): boolean {
    if (!item || !bittiHataliMi(item) || odemeGoreviMi(item, ebeveynTuru) || isaretli(item, isaretler))
        return false;
    if (hashKaydiGeldi(item))
        return !!(item.hash_kaydi && item.hash_kaydi.aktif);
    return gorevHash(item) !== null;
}

/** Sunucunun bildirdiği kayıt durumu; undefined = sunucu bilgisi yok (eski backend). */
function kayitDurumu(item: any): string | null | undefined {
    if (!hashKaydiGeldi(item))
        return undefined;
    const kayit: HashKaydi | null = item.hash_kaydi;
    return kayit && kayit.aktif && doluMetin(kayit.status) ? kayit.status : null;
}

/**
 * Kısıt açıkken çöp kutusu yerine bilgi ikonu. Sunucu durumu varsa ona (backend koruması da
 * kayıt durumuna bakıyor), yoksa / boşsa açıklama metnine bakılır.
 */
export function hashSilmeKilitli(item: any, isaretler: HashSilmeIsaretleri, ebeveynTuru?: number | null): boolean {
    if (!ACILMIS_TAKIP_SILME_KISITI_AKTIF || !hashSilmeIkonuGorunur(item, isaretler, ebeveynTuru))
        return false;
    const durum = kayitDurumu(item);
    return durum ? durum === ACILMIS_TAKIP_STATUS : isAcilmisTakipHatasi(item.description);
}

/** Onay dialog'u gövdesi, kayıt durumuna göre (tevzi / odeme_deneniyor / acildi; diğerleri genel). */
export function kayitDurumuUyarisi(status?: string | null): string {
    switch (status) {
        case ACILMIS_TAKIP_STATUS:
            return ACILMIS_TAKIP_SILME_UYARISI;
        case ODEME_DENENIYOR_STATUS:
            return HASH_SILME_UYARISI_ODEME_DENENIYOR;
        case TEVZI_STATUS:
            return HASH_SILME_UYARISI_TEVZI;
        default:
            return HASH_SILME_UYARISI_GENEL;
    }
}

export function isMukerrerTevziHatasi(message?: string | null): boolean {
    return typeof message === "string" && message.includes(MUKERRER_TEVZI_HATA_ISARETI);
}

/** Hata metnindeki işaretten çıkarılan kayıt durumu; işaret yoksa null. */
function mesajdanKayitDurumu(message?: string | null): string | null {
    if (isAcilmisTakipHatasi(message))
        return ACILMIS_TAKIP_STATUS;
    if (isOdemeDeneniyorHatasi(message))
        return ODEME_DENENIYOR_STATUS;
    return isMukerrerTevziHatasi(message) ? TEVZI_STATUS : null;
}

function derece(durum?: string | null): number {
    return durum ? (KAYIT_DURUMU_DERECESI[durum] || 0) : 0;
}

/** İki durumdan daha riskli olanı; eşitse ilki (sunucu). */
function riskliOlan(birinci?: string | null, ikinci?: string | null): string | null {
    return derece(ikinci) > derece(birinci) ? (ikinci || null) : (birinci || null);
}

/**
 * Detay ekranı onay uyarısı. Önce sunucudaki kayıt durumu; açıklama metnindeki işaret yalnız
 * daha riskli bir uyarıya yükseltebilir (metin eskimiş olabilir, fazla uyarı zararsız).
 */
export function silmeUyarisi(item: any): string {
    return kayitDurumuUyarisi(riskliOlan(kayitDurumu(item), mesajdanKayitDurumu(item && item.description)));
}

/**
 * Sihirbaz satırı (UyapTakipAc / MtsTakipAc) için kayıt durumu tahmini; sunucu bilgisi yok.
 * Açılmış işareti -> acildi, ödeme işareti -> odeme_deneniyor, tevzi bilgisi dolu ya da mükerrer
 * mesajı -> tevzi, aksi halde null (genel metin).
 */
export function sihirbazKayitDurumu(item: any): string | null {
    const mesajlar: any[] = item && Array.isArray(item.messages) ? item.messages : [];
    const mesajVar = (kontrol: (mesaj?: string | null) => boolean) => mesajlar.some(m => m && kontrol(m.message));
    if ((item && item.acilmisTakipBilgisi) || mesajVar(isAcilmisTakipHatasi))
        return ACILMIS_TAKIP_STATUS;
    if (mesajVar(isOdemeDeneniyorHatasi))
        return ODEME_DENENIYOR_STATUS;
    const tevzi = item ? item.tevziBilgileri : null;
    if ((tevzi && typeof tevzi === "object" && Object.keys(tevzi).length > 0) || mesajVar(isMukerrerTevziHatasi))
        return TEVZI_STATUS;
    return null;
}

export function hashSilmeYanitiniYorumla(yanit?: HashSilmeYaniti | null): HashSilmeSonucu {
    if (!yanit || !yanit.success)
        return {tamam: false, toast: "warning", mesaj: (yanit && yanit.message) || HASH_SILINEMEDI_MESAJI};
    if (yanit.silindi === false)
        return {tamam: true, toast: "info", mesaj: yanit.message || HASH_KAYDI_BULUNAMADI_MESAJI};
    return {tamam: true, toast: "success", mesaj: yanit.message || HASH_SILINDI_MESAJI};
}

interface SilmeHttp {
    delete(url: string, config?: any): Promise<any>;
}

interface SilmeToast {
    success(mesaj: string): any;
    info(mesaj: string): any;
    warning(mesaj: string): any;
    error(mesaj: string): any;
}

/**
 * 5 detay ekranı + sihirbaz ActionsColumn'un ortak silme çağrısı. Çözülürse DeleteDialog
 * @success yayınlar; reddedilirse dialog açık kalır.
 */
export async function hashKaydiniSil(http: SilmeHttp, toast: SilmeToast, hash: string | null): Promise<void> {
    if (!hash) {
        toast.warning("Hash bulunamadı.");
        throw new Error("Hash bulunamadı.");
    }
    let res: any;
    try {
        res = await http.delete("/api/v1/uyap-takip-hash", {params: {hash}});
    } catch (e) {
        // HTTP hatasını axios interceptor'ı (StatusCodeHelper) gösteriyor. Yanıtsız (ağ) hatada
        // StatusCodeHelper.error `error.response.status`'ta TypeError'a düşüyor; kullanıcı hiçbir şey görmüyor.
        if (!e || !(e as any).response)
            toast.error(SUNUCUYA_ULASILAMADI_MESAJI);
        throw e;
    }
    const sonuc = hashSilmeYanitiniYorumla(res && res.data);
    toast[sonuc.toast](sonuc.mesaj);
    if (!sonuc.tamam)
        throw new Error(sonuc.mesaj);
}

/** Aynı hash'li BITTI_HATALI satırları görev id'siyle işaretler (anahtar hash değil: yeniden kayıt gizlenmesin). */
export function hashSilindiIsaretle(isaretler: HashSilmeIsaretleri, items: any[], hash: string | null,
                                    sonVerilenSira: number): HashSilmeIsaretleri {
    if (!hash || !Array.isArray(items))
        return isaretler;
    const aranan = hash.toLowerCase();
    const yeni: HashSilmeIsaretleri = {...isaretler};
    items.forEach(item => {
        if (!item || item.id == null || !bittiHataliMi(item))
            return;
        const satirHash = gorevHash(item);
        if (satirHash && satirHash.toLowerCase() === aranan)
            yeni[String(item.id)] = sonVerilenSira;
    });
    return yeni;
}

/**
 * Yanıt yazılırken: silmeden SONRA başlamış (sira > işaret) ve hash_kaydi taşıyan yanıt sunucu
 * doğrusudur, işaret düşer. Eski backend yanıtı işaret düşürmez (yoksa ikon geri gelirdi).
 */
export function isaretleriAyikla(isaretler: HashSilmeIsaretleri, items: any[], sira: number): HashSilmeIsaretleri {
    if (!Array.isArray(items) || !items.some(hashKaydiGeldi))
        return isaretler;
    const kalan: HashSilmeIsaretleri = {};
    Object.keys(isaretler).forEach(id => {
        if (isaretler[id] >= sira)
            kalan[id] = isaretler[id];
    });
    return kalan;
}

/**
 * Ekran başına tek örnek (data alanı). Socket `updateTasks` load()'ları sırasız dönebildiği için
 * yanıt sırasını ve yerel "silindi" işaretlerini tutar. En son BAŞLATILAN değil en son YAZILAN
 * yanıt esas alınır; aksi halde sık gelen olaylarda tablo hiç güncellenmezdi. Vue 2 sınıf
 * örneğini gözlemler; işaretler her seferinde yeni nesneyle atandığı için $set gerekmez.
 */
export class HashSilmeTakibi {
    isaretler: HashSilmeIsaretleri = {};
    verilenSira: number = 0;
    uygulananSira: number = 0;

    /** load() başında. Dönen numara yanıtla birlikte geri verilir. */
    yuklemeBasladi(): number {
        return ++this.verilenSira;
    }

    /** false: daha yeni bir yanıt zaten yazıldı, bu yanıt atılmalı. true: yaz (işaretler ayıklandı). */
    yanitGeldi(sira: number, items: any[]): boolean {
        if (sira < this.uygulananSira)
            return false;
        this.uygulananSira = sira;
        this.isaretler = isaretleriAyikla(this.isaretler, items, sira);
        return true;
    }

    eskiYanitMi(sira: number): boolean {
        return sira < this.uygulananSira;
    }

    sonIstekMi(sira: number): boolean {
        return sira === this.verilenSira;
    }

    /** DeleteDialog @success: aynı hash'li hatalı satırlar hemen gizlenir. */
    silindi(item: any, items: any[]): void {
        this.isaretler = hashSilindiIsaretle(this.isaretler, items, gorevHash(item), this.verilenSira);
    }

    ikonGorunur(item: any, ebeveynTuru?: number | null): boolean {
        return hashSilmeIkonuGorunur(item, this.isaretler, ebeveynTuru);
    }

    kilitli(item: any, ebeveynTuru?: number | null): boolean {
        return hashSilmeKilitli(item, this.isaretler, ebeveynTuru);
    }

    uyari(item: any): string {
        return silmeUyarisi(item);
    }
}
